import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { Panel } from '../components/Panel.js';
import {
  Badge,
  Button,
  DateInput,
  DateSelect,
  datePresetRange,
  Field,
  type DatePreset,
  Icon,
  Modal,
  PageHeader,
  PagedTableShell,
  Select,
  Table,
  Td,
  TextInput,
  useClientPaging,
} from '../components/ui/index.js';
import { readJson, useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';

export interface InvoiceRow {
  id: string;
  number: string;
  status: 'OPEN' | 'PAID' | 'VOID';
  displayStatus: 'OPEN' | 'PARTIAL' | 'PAID' | 'VOID' | 'OVERDUE';
  periodStart: string;
  periodEnd: string;
  amountCents: number;
  paidCents: number;
  balanceCents: number;
  currency: string;
  issuedAt: string;
  dueAt: string;
  paidAt: string | null;
  paymentNote: string | null;
  payments: Array<{ id: string; amountCents: number; paidOn: string; note: string | null }>;
  tenant: { id: string; name: string };
  plan: { code: string; name: string };
}

type Filter = 'ALL' | 'OPEN' | 'PARTIAL' | 'OVERDUE' | 'PAID' | 'VOID';

export function formatMoney(cents: number, currency = 'EUR', locale = 'el-GR'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

function formatDate(value: string | null, locale: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale);
}

export function BillingPage() {
  const { locale, text } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const [filter, setFilter] = useState<Filter>('ALL');
  const [tenantId, setTenantId] = useState('all');
  const [period, setPeriod] = useState<DatePreset>('all');
  const [paying, setPaying] = useState<{ invoice: InvoiceRow; kind: 'full' | 'partial' } | null>(
    null,
  );
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const remote = useRemote<InvoiceRow[]>(
    filter === 'ALL' ? '/v1/platform/invoices' : `/v1/platform/invoices?status=${filter}`,
    null,
  );
  const tenants = useRemote<Array<{ id: string; name: string; creditDays?: number }>>(
    '/v1/tenants',
    null,
  );
  const periodRange = datePresetRange(period);
  const visible = (remote.data ?? []).filter((invoice) => {
    if (tenantId !== 'all' && invoice.tenant.id !== tenantId) return false;
    if (!periodRange) return true;
    const due = new Date(invoice.dueAt);
    if (Number.isNaN(due.getTime())) return false;
    const day = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}-${String(due.getDate()).padStart(2, '0')}`;
    return day >= periodRange.from && day <= periodRange.to;
  });
  const paging = useClientPaging(visible, { resetKey: `${filter}:${tenantId}:${period}` });
  const invoiceCount = visible.length;
  const invoiceCountLabel =
    invoiceCount === 1
      ? text('1 τιμολόγιο', '1 invoice')
      : text(
          `${invoiceCount.toLocaleString(intlLocale)} τιμολόγια`,
          `${invoiceCount.toLocaleString(intlLocale)} invoices`,
        );
  const amountByCurrency = new Map<string, number>();
  for (const invoice of visible) {
    amountByCurrency.set(
      invoice.currency,
      (amountByCurrency.get(invoice.currency) ?? 0) + invoice.amountCents,
    );
  }
  const amountTotal = [...amountByCurrency.entries()]
    .map(([currency, cents]) => formatMoney(cents, currency, intlLocale))
    .join(' · ');
  const tenantOptions = [...(tenants.data ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name, 'el'),
  );

  async function issueMonth(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const result = await readJson<{ issued: number; existing: number; failed: number }>(
        '/v1/platform/invoices/issue',
        { method: 'POST' },
        null,
      );
      setNotice(
        text(
          `Εκδόθηκαν ${result.issued}. Ήδη υπήρχαν ${result.existing}. Αποτυχίες ${result.failed}.`,
          `Issued ${result.issued}. Already existed: ${result.existing}. Failed: ${result.failed}.`,
        ),
      );
      void remote.mutate();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η έκδοση απέτυχε', 'Issuing failed'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="admin-page">
      <PageHeader
        description={text(
          'Χρέωση τιμολογίων ανά μήνα και η πληρωμή της. Η ανάθεση πακέτου με τιμή εκδίδει το τιμολόγιο του μήνα.',
          'Monthly invoicing and payment tracking. Assigning a priced plan issues the invoice for the month.',
        )}
        actions={
          <>
            <Button type="button" onClick={() => setCreating(true)}>
              <Icon name="plus" className="size-4" /> {text('Νέο τιμολόγιο', 'New invoice')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={pending}
              onClick={() => void issueMonth()}
            >
              <Icon name="file" className="size-4" />{' '}
              {text('Έκδοση τιμολογίων μήνα', 'Issue monthly invoices')}
            </Button>
          </>
        }
      />
      <ol className="grid gap-2 text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2 xl:grid-cols-4">
        <li className="admin-surface px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          {text('1. Δημιουργία πελάτη', '1. Create a customer')}
        </li>
        <li className="admin-surface px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          {text('2. Ανάθεση πακέτου με μηνιαία τιμή', '2. Assign a plan with a monthly price')}
        </li>
        <li className="admin-surface px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          {text('3. Πλήρης ή μερική πληρωμή', '3. Record a full or partial payment')}
        </li>
        <li className="admin-surface px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          {text('4. Νέος μήνας: έκδοση τιμολογίων', '4. New month: issue invoices')}
        </li>
      </ol>
      {notice ? <p className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p> : null}
      {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select
          aria-label={text('Κατάσταση τιμολογίου', 'Invoice status')}
          className="w-full sm:w-56"
          value={filter}
          onChange={(next) => setFilter(next as Filter)}
          options={[
            { value: 'ALL', label: text('Όλα', 'All') },
            { value: 'OPEN', label: text('Απλήρωτα', 'Unpaid') },
            { value: 'PARTIAL', label: text('Μερικά', 'Partial') },
            { value: 'OVERDUE', label: text('Εκπρόθεσμα', 'Overdue') },
            { value: 'PAID', label: text('Πληρωμένα', 'Paid') },
            { value: 'VOID', label: text('Ακυρωμένα', 'Void') },
          ]}
        />
        <Select
          aria-label={text('Πελάτης', 'Customer')}
          className="w-full sm:w-72"
          value={tenantId}
          onChange={setTenantId}
          options={[
            { value: 'all', label: text('Όλοι οι πελάτες', 'All customers') },
            ...tenantOptions.map((tenant) => ({ value: tenant.id, label: tenant.name })),
          ]}
        />
        <DateSelect
          aria-label={text('Περίοδος λήξης', 'Due period')}
          className="w-full sm:w-64"
          value={period}
          onChange={setPeriod}
        />
      </div>
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={paging.total === 0}
        emptyLabel={
          tenantId === 'all'
            ? text('Δεν υπάρχουν τιμολόγια σε αυτή την προβολή.', 'No invoices in this view.')
            : text('Δεν υπάρχουν τιμολόγια για αυτόν τον πελάτη.', 'No invoices for this customer.')
        }
        emptyHint={text(
          'Ανάθεσε πακέτο με τιμή ή πάτησε «Έκδοση τιμολογίων μήνα».',
          'Assign a priced plan or select “Issue monthly invoices”.',
        )}
      >
        <div className="admin-surface overflow-hidden dark:border-slate-700 dark:bg-slate-900">
          <PagedTableShell pagination={paging.total > 0 ? paging.pager : null}>
            <Table
              head={[
                text('Τιμολόγιο', 'Invoice'),
                text('Πελάτης', 'Customer'),
                text('Περίοδος', 'Period'),
                text('Ποσό', 'Amount'),
                text('Λήξη', 'Due date'),
                text('Κατάσταση', 'Status'),
                '',
              ]}
              foot={
                <tr>
                  <Td className="whitespace-nowrap">
                    {text('Σύνολο', 'Total')} · {invoiceCountLabel}
                  </Td>
                  <Td />
                  <Td />
                  <Td className="font-semibold">
                    {amountTotal || formatMoney(0, 'EUR', intlLocale)}
                  </Td>
                  <Td />
                  <Td />
                  <Td />
                </tr>
              }
            >
              {paging.rows.map((invoice) => (
                <InvoiceRowView
                  key={invoice.id}
                  invoice={invoice}
                  intlLocale={intlLocale}
                  onPay={(kind) => setPaying({ invoice, kind })}
                />
              ))}
            </Table>
          </PagedTableShell>
        </div>
      </Panel>
      <p className="text-sm text-slate-500">
        {text(
          'Απλήρωτο τιμολόγιο δεν σταματά μόνο του το chatbot. Η αναστολή γίνεται από την καρτέλα του πελάτη.',
          'An unpaid invoice does not stop the chatbot automatically. Suspend it from the customer page.',
        )}
      </p>
      {creating ? (
        <NewInvoiceDialog
          tenants={tenantOptions}
          initialTenantId={tenantId === 'all' ? (tenantOptions[0]?.id ?? '') : tenantId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            setNotice(text('Το τιμολόγιο εκδόθηκε.', 'The invoice was issued.'));
            void remote.mutate();
          }}
        />
      ) : null}
      {paying ? (
        <InvoicePaymentDialog
          invoice={paying.invoice}
          kind={paying.kind}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            setNotice(text('Η πληρωμή καταχωρίστηκε.', 'The payment was recorded.'));
            void remote.mutate();
          }}
        />
      ) : null}
    </section>
  );
}

const INVOICE_STATUS_LABEL: Record<InvoiceRow['displayStatus'], { el: string; en: string }> = {
  OPEN: { el: 'Απλήρωτο', en: 'Unpaid' },
  PARTIAL: { el: 'Μερική πληρωμή', en: 'Partially paid' },
  OVERDUE: { el: 'Εκπρόθεσμο', en: 'Overdue' },
  PAID: { el: 'Πληρωμένο', en: 'Paid' },
  VOID: { el: 'Ακυρωμένο', en: 'Void' },
};

function InvoiceRowView(props: {
  invoice: InvoiceRow;
  intlLocale: string;
  onPay: (kind: 'full' | 'partial') => void;
}) {
  const { text } = useAdminLocale();
  const { invoice } = props;
  const [openHistory, setOpenHistory] = useState(false);
  const payments = invoice.payments ?? [];
  return (
    <>
      <tr>
        <Td>
          <span className="font-medium text-slate-900 dark:text-slate-100">{invoice.number}</span>
          <span className="mt-0.5 block text-xs text-slate-500">{invoice.plan.name}</span>
        </Td>
        <Td>
          <Link
            className="font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200"
            to={`/platform/tenants/${invoice.tenant.id}`}
          >
            {invoice.tenant.name}
          </Link>
        </Td>
        <Td muted>
          {formatDate(invoice.periodStart, props.intlLocale)} –{' '}
          {formatDate(invoice.periodEnd, props.intlLocale)}
        </Td>
        <Td>
          <span className="font-medium">
            {formatMoney(invoice.amountCents, invoice.currency, props.intlLocale)}
          </span>
          {invoice.balanceCents > 0 && invoice.status !== 'VOID' ? (
            <span className="mt-0.5 block text-xs text-amber-700 dark:text-amber-300">
              {text('Υπόλοιπο', 'Balance')}{' '}
              {formatMoney(invoice.balanceCents, invoice.currency, props.intlLocale)}
            </span>
          ) : invoice.status === 'PAID' ? (
            <span className="mt-0.5 block text-xs text-emerald-700 dark:text-emerald-300">
              {text('Εξοφλημένο', 'Paid in full')}
            </span>
          ) : null}
          {payments.length > 0 ? (
            <button
              type="button"
              className="mt-1 text-xs font-medium text-blue-600 hover:underline"
              onClick={() => setOpenHistory((value) => !value)}
            >
              {openHistory
                ? text('Απόκρυψη ιστορικού', 'Hide history')
                : text(`Ιστορικό (${payments.length})`, `History (${payments.length})`)}
            </button>
          ) : null}
        </Td>
        <Td muted>{formatDate(invoice.dueAt, props.intlLocale)}</Td>
        <Td>
          <Badge
            value={invoice.displayStatus}
            label={text(
              INVOICE_STATUS_LABEL[invoice.displayStatus].el,
              INVOICE_STATUS_LABEL[invoice.displayStatus].en,
            )}
          />
        </Td>
        <Td className="text-right">
          {invoice.balanceCents > 0 && invoice.status !== 'VOID' ? (
            <span className="inline-flex flex-wrap justify-end gap-2">
              <Button type="button" onClick={() => props.onPay('full')}>
                <Icon name="banknote" className="size-4" /> {text('Πλήρης', 'Full')}
              </Button>
              <Button type="button" variant="secondary" onClick={() => props.onPay('partial')}>
                <Icon name="coins" className="size-4" /> {text('Μερική', 'Partial')}
              </Button>
            </span>
          ) : null}
        </Td>
      </tr>
      {openHistory ? (
        <tr className="bg-slate-50 dark:bg-slate-950/40">
          <Td colSpan={7}>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              {text('Ιστορικό πληρωμών', 'Payment history')}
            </p>
            <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
              {payments.map((payment) => (
                <li key={payment.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-medium">
                    {formatMoney(payment.amountCents, invoice.currency, props.intlLocale)}
                  </span>
                  <time dateTime={payment.paidOn}>
                    {formatDate(payment.paidOn, props.intlLocale)}
                  </time>
                  {payment.note ? <span className="text-slate-500">{payment.note}</span> : null}
                </li>
              ))}
            </ul>
          </Td>
        </tr>
      ) : null}
    </>
  );
}

function dateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function dueAfter(days: number, from = new Date()): string {
  const due = new Date(from);
  due.setDate(due.getDate() + days);
  return dateInput(due);
}

function defaultInvoiceDates(creditDays = 14): { start: string; end: string; due: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return { start: dateInput(start), end: dateInput(end), due: dueAfter(creditDays, now) };
}

function NewInvoiceDialog(props: {
  tenants: Array<{ id: string; name: string; creditDays?: number }>;
  initialTenantId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { text } = useAdminLocale();
  const initialCredit =
    props.tenants.find((tenant) => tenant.id === props.initialTenantId)?.creditDays ?? 14;
  const dates = defaultInvoiceDates(initialCredit);
  const [tenantId, setTenantId] = useState(props.initialTenantId);
  const [amount, setAmount] = useState('');
  const [periodStart, setPeriodStart] = useState(dates.start);
  const [periodEnd, setPeriodEnd] = useState(dates.end);
  const [dueAt, setDueAt] = useState(dates.due);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!tenantId) {
      setError(text('Επίλεξε πελάτη.', 'Select a customer.'));
      return;
    }
    const cents = eurosToCents(amount);
    if (cents === null) {
      setError(
        text(
          'Γράψε ένα θετικό ποσό με έως δύο δεκαδικά.',
          'Enter a positive amount with up to two decimal places.',
        ),
      );
      return;
    }
    if (!periodStart || !periodEnd || !dueAt) {
      setError(
        text(
          'Συμπλήρωσε την περίοδο και την ημερομηνία λήξης.',
          'Complete the billing period and due date.',
        ),
      );
      return;
    }
    if (periodEnd <= periodStart) {
      setError(
        text(
          'Η λήξη περιόδου πρέπει να είναι μετά την έναρξη.',
          'The period end must be after the start.',
        ),
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/platform/invoices',
        {
          method: 'POST',
          body: JSON.stringify({
            tenantId,
            amountCents: cents,
            periodStart,
            periodEnd,
            dueAt,
          }),
        },
        null,
      );
      props.onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η έκδοση απέτυχε', 'Issuing failed'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      title={text('Νέο τιμολόγιο', 'New invoice')}
      description={text(
        'Χειροκίνητη έκδοση για έναν πελάτη. Δεν γίνεται χρέωση κάρτας. Ο πελάτης χρειάζεται πακέτο.',
        'Issue an invoice manually for one customer. No card is charged. The customer must have a plan.',
      )}
      onClose={props.onClose}
    >
      <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
        <Field label={text('Πελάτης', 'Customer')}>
          <Select
            className="mt-1.5"
            required
            value={tenantId}
            onChange={(next) => {
              setTenantId(next);
              const days = props.tenants.find((tenant) => tenant.id === next)?.creditDays ?? 14;
              setDueAt(dueAfter(days));
            }}
            options={[
              ...(tenantId === ''
                ? [{ value: '', label: text('Επίλεξε πελάτη', 'Select a customer') }]
                : []),
              ...props.tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
            ]}
          />
        </Field>
        <Field
          label={text('Ποσό (EUR)', 'Amount (EUR)')}
          hint={text(
            'Θετικό ποσό με έως δύο δεκαδικά.',
            'Positive amount with up to two decimal places.',
          )}
        >
          <TextInput
            required
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="10.00"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={text('Έναρξη περιόδου', 'Period start')}>
            <DateInput
              aria-label={text('Έναρξη περιόδου', 'Period start')}
              value={periodStart}
              clearable={false}
              onChange={setPeriodStart}
            />
          </Field>
          <Field
            label={text('Λήξη περιόδου', 'Period end')}
            hint={text(
              'Η πρώτη μέρα μετά το διάστημα που χρεώνεις.',
              'The first day after the billed period.',
            )}
          >
            <DateInput
              aria-label={text('Λήξη περιόδου', 'Period end')}
              value={periodEnd}
              clearable={false}
              onChange={setPeriodEnd}
            />
          </Field>
        </div>
        <Field
          label={text('Ημερομηνία λήξης', 'Due date')}
          hint={text('Προεπιλογή 14 μέρες από σήμερα.', 'Defaults to 14 days from today.')}
        >
          <DateInput
            aria-label={text('Ημερομηνία λήξης', 'Due date')}
            value={dueAt}
            clearable={false}
            onChange={setDueAt}
          />
        </Field>
        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            <Icon name="x" className="size-4" /> {text('Άκυρο', 'Cancel')}
          </Button>
          <Button type="submit" disabled={pending || props.tenants.length === 0}>
            <Icon name="file" className="size-4" />{' '}
            {pending ? text('Έκδοση…', 'Issuing…') : text('Έκδοση', 'Issue')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function todayInput(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function eurosToCents(value: string): number | null {
  const raw = value.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
  const cents = Math.round(Number(raw) * 100);
  return cents >= 1 ? cents : null;
}

export function InvoicePaymentDialog(props: {
  invoice: InvoiceRow;
  kind: 'full' | 'partial';
  onClose: () => void;
  onSaved: () => void;
}) {
  const { locale, text } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const balance = props.invoice.balanceCents;
  const [amount, setAmount] = useState(props.kind === 'full' ? (balance / 100).toFixed(2) : '');
  const [note, setNote] = useState('');
  const [paidOn, setPaidOn] = useState(todayInput());
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const cents = eurosToCents(amount);
    if (cents === null) {
      setError(
        text(
          'Γράψε ένα θετικό ποσό με έως δύο δεκαδικά.',
          'Enter a positive amount with up to two decimal places.',
        ),
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        `/v1/tenants/${props.invoice.tenant.id}/invoices/${props.invoice.id}/payments`,
        {
          method: 'POST',
          body: JSON.stringify({ amountCents: cents, paidOn, note: note.trim() || null }),
        },
        null,
      );
      props.onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η καταχώριση απέτυχε', 'Recording failed'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      title={
        props.kind === 'full'
          ? text('Πλήρης εξόφληση', 'Full payment')
          : text('Μερική πληρωμή', 'Partial payment')
      }
      description={text(
        `${props.invoice.number} · υπόλοιπο ${formatMoney(balance, props.invoice.currency, intlLocale)}`,
        `${props.invoice.number} · balance ${formatMoney(balance, props.invoice.currency, intlLocale)}`,
      )}
      onClose={props.onClose}
    >
      <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
        <Field label={text('Ποσό (EUR)', 'Amount (EUR)')}>
          <TextInput
            required
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field label={text('Ημερομηνία', 'Date')}>
          <DateInput
            aria-label={text('Ημερομηνία πληρωμής', 'Payment date')}
            value={paidOn}
            max={todayInput()}
            clearable={false}
            onChange={setPaidOn}
          />
        </Field>
        <Field label={text('Σημείωση', 'Note')}>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={500}
            rows={3}
            className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
            placeholder={text('π.χ. κατάθεση', 'e.g. bank transfer')}
          />
        </Field>
        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            <Icon name="x" className="size-4" /> {text('Άκυρο', 'Cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            <Icon name="check" className="size-4" /> {text('Καταχώριση', 'Record')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
